const API_URL = process.env.NEXT_PUBLIC_API_URL || '';

interface FetchOptions extends Omit<RequestInit, 'body'> {
  // null accepté : useAuth() expose `token: string | null` et le helper traite
  // déjà l'absence de token par un test de véracité (cf. `if (token)` plus bas).
  token?: string | null;
  // api() sérialise lui-même les bodies objet (cf. plus bas), ce que le type
  // RequestInit['body'] n'autorise pas — d'où le Omit ci-dessus.
  body?: RequestInit['body'] | Record<string, unknown>;
}

async function refreshAccessToken(): Promise<string | null> {
  const refreshToken = localStorage.getItem('refreshToken');
  if (!refreshToken) return null;
  try {
    const res = await fetch(`${API_URL}/api/auth/refresh`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refreshToken }),
    });
    if (!res.ok) return null;
    const data = await res.json();
    localStorage.setItem('accessToken', data.accessToken);
    return data.accessToken;
  } catch {
    return null;
  }
}

export async function api<T>(path: string, options: FetchOptions = {}): Promise<T> {
  const { token, ...fetchOptions } = options;
  const headers: Record<string, string> = {
    ...((fetchOptions.headers as Record<string, string>) || {}),
  };
  if (token) headers['Authorization'] = `Bearer ${token}`;
  if (fetchOptions.body && typeof fetchOptions.body === 'object') { headers['Content-Type'] = 'application/json'; fetchOptions.body = JSON.stringify(fetchOptions.body); } else if (fetchOptions.body) { headers['Content-Type'] = 'application/json'; }

  // Après la sérialisation ci-dessus, body est forcément un BodyInit valide.
  const init: RequestInit = { ...fetchOptions, body: fetchOptions.body as RequestInit['body'] };

  let res = await fetch(`${API_URL}${path}`, { ...init, headers });

  // Si 401, tenter refresh puis retry
  if (res.status === 401 && token) {
    const newToken = await refreshAccessToken();
    if (newToken) {
      headers['Authorization'] = `Bearer ${newToken}`;
      res = await fetch(`${API_URL}${path}`, { ...init, headers });
      // Mettre à jour le token dans le provider via event
      window.dispatchEvent(new CustomEvent('token-refreshed', { detail: newToken }));
    } else {
      // Refresh échoué → déconnexion
      localStorage.removeItem('accessToken');
      localStorage.removeItem('refreshToken');
      localStorage.removeItem('user');
      window.location.href = '/';
      throw new Error('Session expirée');
    }
  }

  if (!res.ok) {
    const error = await res.json().catch(() => ({ error: 'Erreur serveur' }));
    throw new Error(error.error || `Erreur ${res.status}`);
  }
  return res.json();
}
